import axios from 'axios';
import { Map } from 'immutable';
import React, { useEffect, useRef, useState } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import i18n from '@i18n';
import { getUrl } from '@/common/oldMobile';
import PopupNg from '@/ui/PopupNg';
import { catcher } from '@/_http/utils';
import { HOME } from '@/routing/routes';
import * as nikeformjs from 'nike-form-js';
import { onChangedErrors } from '@/common/components/nikeforms/utils';
import { fireGAEvent } from '@/analytics';
import Input from '@/common/components/nikeforms/Input';
import { Submit } from '@/common/components/nikeforms/Submit';
import { PATTERN_EMAIL, patterns } from '@/common/validations';
import FormUploadField from '@/contactUs/views/FormUploadField';
import { cleanupDocUploads, getAccountCancelRequestDocConfig } from './handlers';
import { accountActionAllowed$, accountUserData$, loggedIn$ } from '@/account/subs';
import { LoginForm } from '@/loginng/views/LoginDialog';
import { ParamArticle } from '@/article/views/Article';

import './AccountCancelRequest.less';

const validators = [
    [['email'], strValue => (PATTERN_EMAIL.test(strValue) ? null : i18n('error.validacia.formular.registracia.email'))],
    [
        ['phone'],
        strValue => (patterns.PHONE.test(strValue) ? null : i18n('error.validacia.formular.zmenatelefonu.telefon'))
    ]
];

function useForm(idZak, email, phone, attachmentFile1, attachmentFile2) {
    const [form] = useState(() => {
        const reason = i18n('account.cancel.label.reason');
        const initData = Map({ email, idZak, phone, reason });
        return nikeformjs.makeForm(initData, {
            validator: nikeformjs.validator(validators, errors => errors),
            onFormUpdated: (form, oldForm) => {
                onChangedErrors(form, oldForm, (path, error) => {
                    fireGAEvent('Zrusenie hracskeho konta', path, error);
                });
            }
        });
    });
    const submitForm = nikeformjs.handleValidForm(form, formValues => {
        const values = formValues.toJS();
        const formData = new FormData();
        formData.append('email', values.email);
        formData.append('phone', values.phone);
        formData.append('reason', values.reason);
        formData.append('message', values.message || '');
        formData.append('idZak', values.idZak || idZak || '');
        if (values.document1) {
            formData.append('docVersions', values.document1);
        }
        if (values.document2) {
            formData.append('docVersions', values.document2);
        }
        if (attachmentFile1.current) {
            formData.append('files', attachmentFile1.current);
        }
        if (attachmentFile2.current) {
            formData.append('files', attachmentFile2.current);
        }

        axios
            .post(getUrl('ACCOUNT_CANCEL_SUBMIT'), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            .then(() => {
                PopupNg.dispatcher.show({
                    messages: 'account.cancel.success.server',
                    close: ['navigate', HOME]
                });
            })
            .catch(catcher.handleMasterError);
    });
    return [form, submitForm];
}

function AccounCancelRequest() {
    const { derefSub, subscribe } = useReframe();
    const loggedIn = subscribe(loggedIn$);
    const attachmentFile1 = useRef();
    const attachmentFile2 = useRef();
    useEffect(() => {
        if (loggedIn) {
            dispatch(getAccountCancelRequestDocConfig);
        }
        return () => dispatch(cleanupDocUploads);
    }, [loggedIn]);

    function onFileChange(e, attachmentFile) {
        const files = e.target.files;
        attachmentFile.current = files[0];
    }

    const { idZak, email, phone, cancelRequest } = subscribe(accountUserData$);
    const [form, submitForm] = useForm(idZak, email, phone, attachmentFile1, attachmentFile2);
    const showContractIdAllowed = derefSub(accountActionAllowed$('SHOW_CONTRACT_ID'));

    if (!loggedIn) {
        return <NotLoggedInAccountCancel />;
    }

    if (!cancelRequest) {
        return <NotCancelRequest />;
    }

    return (
        <div className="page-wrap layout-rw">
            <form className="nike-form" onSubmit={submitForm}>
                <h2 className="pb-15 pt-10">{i18n('account.cancel.form.title')}</h2>
                <div className="fs-14 pb-10">{i18n('account.cancel.form.info')}</div>
                {showContractIdAllowed ? (
                    <Input
                        form={form}
                        path={['idZak']}
                        label={idZak ? i18n('label.cisloZmluvy') : i18n('account.cancel.label.cisloZmluvy')}
                        disabled={!!idZak}
                    />
                ) : null}
                <Input form={form} path={['phone']} label={i18n('account.cancel.label.phone')} disabled={true} />
                <Input form={form} path={['email']} label={i18n('account.cancel.label.email')} disabled={true} />
                <Input
                    form={form}
                    path={['reason']}
                    label={i18n('account.cancel.label.reason.label')}
                    disabled={true}
                />
                <Input
                    type="textarea"
                    rows={3}
                    form={form}
                    path={['message']}
                    label={i18n('account.cancel.label.message')}
                    placeholder={i18n('account.cancel.label.message.option.text.default')}
                />
                <div className="nike-input-label">{i18n('account.cancel.label.attachments')}</div>
                {loggedIn ? (
                    <>
                        <FormUploadField form={form} path={['document1']} slot={0} />
                        <FormUploadField form={form} path={['document2']} slot={1} />
                    </>
                ) : (
                    <>
                        <label className="nike-input">
                            <input type="file" multiple={false} onChange={e => onFileChange(e, attachmentFile1)} />
                        </label>
                        <label className="nike-input">
                            <div className="nike-input-label">{i18n('account.cancel.label.attachments2')}</div>
                            <input type="file" multiple={false} onChange={e => onFileChange(e, attachmentFile2)} />
                        </label>
                    </>
                )}
                <Submit form={form} showError={false} label={i18n('account.cancel.label.action')} forceEnabled />
            </form>
        </div>
    );
}

function NotLoggedInAccountCancel() {
    return (
        <div className="account-cancel-container">
            <h2>{i18n('account-cancel.not-logged')}</h2>
            <p>{i18n('account-cancel.not-logged.description')}</p>
            <LoginForm registerInfo={false} loginOrigin="inPage" />
        </div>
    );
}

function NotCancelRequest() {
    return <ParamArticle articleCode={'zrusenie-konta'} />;
}

export default AccounCancelRequest;
